import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useOfflineSession } from '../context/OfflineContext';
import { useNoSignal, useOnSignalBack, useSavedCopy, reportSavedCopy } from '../utils/connectivity';

const bannerStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.55rem 1rem',
  background: '#7a4a00',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: '600',
  flexShrink: 0,
};

const BACK_ONLINE_MS = 4000;

const formatTime = (at) => new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

// Used by a page that fell back to its locally saved copy because live data couldn't be fetched.
// It draws nothing itself: it tells the app-wide banner below how old that copy is (so the
// musician never sees old data passed off as current), and refetches when the signal returns.
function OfflineBanner({ cachedAt, label, onSignalBack }) {
  useEffect(() => (cachedAt ? reportSavedCopy({ cachedAt, label }) : undefined), [cachedAt, label]);
  useOnSignalBack(onSignalBack);
  return null;
}

// The one connection banner across the dashboard. Uses a fixed warning colour rather than the
// band's theme, matching how other status indicators (error toasts etc.) are handled in this app.
//  - No signal (a cold start with no signal, or it dropped mid-session): saved gigs, set lists and
//    lyrics work, anything that saves doesn't.
//  - Signal just came back: says so briefly while the page fetches fresh data.
//  - Signal but the page still couldn't load (the server's down, say): says it's a saved copy.
export function OfflineModeBanner() {
  const { offlineSession } = useOfflineSession();
  const noSignal = useNoSignal();
  const savedCopy = useSavedCopy();
  const [backOnline, setBackOnline] = useState(false);

  useOnSignalBack(() => setBackOnline(true));
  useEffect(() => {
    if (!backOnline) return;
    const timer = setTimeout(() => setBackOnline(false), BACK_ONLINE_MS);
    return () => clearTimeout(timer);
  }, [backOnline]);

  const saved = savedCopy ? `${savedCopy.label} saved at ${formatTime(savedCopy.cachedAt)}` : 'saved gig data';

  if (offlineSession || noSignal) {
    return (
      <div style={bannerStyle}>
        <WifiOff size={15} style={{ flexShrink: 0 }} />
        <span>No signal: showing {saved}. Changes need signal.</span>
      </div>
    );
  }
  if (backOnline) {
    return (
      <div style={{ ...bannerStyle, background: '#1f6b3a' }}>
        <Wifi size={15} style={{ flexShrink: 0 }} />
        <span>Back online: updating…</span>
      </div>
    );
  }
  if (savedCopy) {
    return (
      <div style={bannerStyle}>
        <WifiOff size={15} style={{ flexShrink: 0 }} />
        <span>Couldn't reach GigRequest: showing {saved}.</span>
      </div>
    );
  }
  return null;
}

export default OfflineBanner;
